
import { Component, Vue, Watch } from "vue-property-decorator";
import Sidebar from "@/components/navigations/sidebar.vue";
import Cta from "@/components/buttons/Button.vue";
import Card from "@/components/card/card.vue";
import Tabs from "@/components/tabs/Tab.vue";
import {
  BenevoleDetail,
  CompetenceGroup,
  CompetenceItem,
  ElementKey,
  HabilitationCategory,
  Habilitation,
  PiwigoLevel,
} from "@/models";
import { benevoles, data, login } from "@/store";
import { found } from "@/helpers";
import BenevoleService from "@/services/benevole.service";
import debounce from "lodash/debounce";

@Component({
  methods: { found },
  components: { Sidebar, Cta, Card, Tabs },
})
export default class BenevoleView extends Vue {
  data: BenevoleDetail | null = null;
  dataReady = false;
  hasUpdated = false;
  tabSelected = "fiche";
  readonly allTabs = [
    {
      key: "fiche",
      label: "Fiche",
      allowedRoles: ["ADMIN", "USR:VIEW", "USR:EDIT"],
    },
    {
      key: "competences",
      label: "Compétences",
      allowedRoles: ["ADMIN", "USR:COMP"],
    },
    {
      key: "habilitations",
      label: "Habilitations",
      allowedRoles: ["ADMIN", "USR:HAB"],
    },
  ];

  habilitationCategories: HabilitationCategory[] = [];
  habilitationLoaded = false;
  piwigoAccess: PiwigoLevel = "NONE";
  piwigoSaving = false;
  roleSaving = false;

  readonly piwigoOptions: {
    value: PiwigoLevel;
    label: string;
    description: string;
  }[] = [
    {
      value: "NONE",
      label: "Consulter",
      description:
        "Permet de voir l'ensemble des photos de la photothèque, sans pouvoir en ajouter, modifier ou supprimer, ni accéder à l'administration de Piwigo.",
    },
    {
      value: "GESTIONNAIRE",
      label: "Gestionnaire",
      description: "Permet de gérer les albums et les photos.",
    },
    {
      value: "ADMIN",
      label: "Administrateur",
      description:
        "Paramétrage complet de la photothèque et gestion des albums et des photos. (droit complet, à réserver à des utilisateurs exceptionnels)",
    },
  ];

  readonly habilitationFilter: Record<string, string[] | null> = {
    FC: [],
    SPACES: [],
    GEST_ACTI: ["GA:VIEW", "GA:CREATE"],
    DECL: [],
    USR: null,
    OTHER: ["RSP_MINEUR"],
    "GEST:TECH": null,
    V2: [],
  };

  private saveComment!: (c: CompetenceItem) => void;

  created(): void {
    this.saveComment = debounce(async (c: CompetenceItem) => {
      const userId = this.$route.params.id;
      await BenevoleService.updateCompetence(
        userId,
        c.id,
        c.step.id,
        c.comment,
      );
      this.$set(c, "commentSaving", false);
      this.$set(c, "commentIsSaved", true);
      setTimeout(() => this.$set(c, "commentIsSaved", false), 2000);
    }, 1500);
  }

  async mounted(): Promise<void> {
    await this.getDatas();
    this.hasUpdated = false;
    this.dataReady = true;
    const firstTab = this.tabs[0]?.key;
    if (firstTab && firstTab !== "fiche") {
      this.tabSelected = firstTab;
    }
  }

  private async getDatas(): Promise<void> {
    await benevoles.benevole(this.$route.params.id);
    await data.roles();
    this.data = ({ ...benevoles.getBenevole } as BenevoleDetail) ?? null;
  }

  private selectDroit(selected: ElementKey): void {
    if (this.data) this.data.role = selected.id;
  }

  private async cancelUpdate(): Promise<void> {
    await this.getDatas();
    this.hasUpdated = false;
  }

  private async update(): Promise<void> {
    if (this.data) {
      await BenevoleService.update(this.data);
      await this.getDatas();
      this.hasUpdated = false;
    }
  }

  private toggleMineur(): void {
    if (this.data?.mineur) {
      this.data.mineur.statut = !this.data.mineur.statut;
      if (!this.data.mineur.statut) {
        this.data.mineur.heure_limite = null;
      }
    }
  }

  private async changeStep(c: CompetenceItem, event: Event): Promise<void> {
    const newStepId = (event.target as HTMLSelectElement).value;
    const step = this.data?.competences_steps?.find((s) => s.id === newStepId);
    if (step) {
      this.$set(c, "step", { ...step });
    }
    await BenevoleService.updateCompetence(
      this.$route.params.id,
      c.id,
      newStepId,
      c.comment,
    );
  }

  private toggleComment(c: CompetenceItem): void {
    this.$set(c, "hasComment", !c.hasComment);
  }

  private onCommentInput(groupId: string, c: CompetenceItem): void {
    this.$set(c, "commentSaving", true);
    this.$set(c, "commentIsSaved", false);
    this.saveComment(c);
  }

  countDoublure(group: CompetenceGroup): number {
    return group.competence.filter((c) => c.step.id.includes("DBLR")).length;
  }

  countAcquis(group: CompetenceGroup): number {
    return group.competence.filter((c) => c.step.id === "ACQUIS").length;
  }

  get canEdit(): boolean {
    const ctx = login.getContext;
    if (!ctx) return false;
    return (
      ctx.roles.includes("ADMIN") || ctx.habilitations.includes("USR:EDIT")
    );
  }

  get canEditRole(): boolean {
    const ctx = login.getContext;
    if (!ctx) return false;
    return ctx.roles.includes("ADMIN") || ctx.habilitations.includes("USR:HAB");
  }

  get tabs(): { key: string; label: string }[] {
    const context = login.getContext;
    return this.allTabs.filter((tab) => {
      if (!("allowedRoles" in tab)) return true;
      return (tab.allowedRoles as string[]).some(
        (r) => context?.roles.includes(r) || context?.habilitations.includes(r),
      );
    });
  }

  get filteredHabilitationCategories(): HabilitationCategory[] {
    return this.habilitationCategories
      .map((cat) => {
        const allowed = this.habilitationFilter[cat.id];
        if (Array.isArray(allowed) && allowed.length === 0) return null;
        const habilitations =
          allowed == null
            ? cat.habilitations
            : cat.habilitations.filter((h) => allowed.includes(h.id));
        return { ...cat, habilitations };
      })
      .filter(
        (cat): cat is HabilitationCategory =>
          cat !== null && cat.habilitations.length > 0,
      );
  }

  get benevole(): BenevoleDetail | null {
    return benevoles.getBenevole as BenevoleDetail;
  }

  get roles(): ElementKey[] | null {
    return data.getRoles;
  }

  @Watch("tabSelected")
  async onTabChange(tab: string): Promise<void> {
    if (tab === "habilitations" && !this.habilitationLoaded) {
      const userId = this.$route.params.id;
      const [habs, piwigo] = await Promise.allSettled([
        BenevoleService.habilitations(userId),
        BenevoleService.piwigoGetAccess(userId),
      ]);
      this.habilitationCategories =
        habs.status === "fulfilled" ? habs.value : [];
      this.piwigoAccess = piwigo.status === "fulfilled" ? piwigo.value : "NONE";
      this.habilitationLoaded = true;
    }
  }

  private isHabBlocked(hab: Habilitation): boolean {
    const prereqs: Record<string, string> = {
      "USR:EDIT": "USR:VIEW",
      "USR:HAB": "USR:VIEW",
    };
    const prereq = prereqs[hab.id];
    if (!prereq) return false;
    for (const cat of this.habilitationCategories) {
      const found = cat.habilitations.find((h) => h.id === prereq);
      if (found) return !found.active;
    }
    return false;
  }

  async onToggleHabilitation(hab: Habilitation): Promise<void> {
    if (!hab.active && this.isHabBlocked(hab)) return;
    const newActive = !hab.active;
    await BenevoleService.toggleHabilitation(
      this.$route.params.id,
      hab.id,
      newActive,
    );
    this.$set(hab, "active", newActive);
  }

  async onSetPiwigoAccess(level: PiwigoLevel): Promise<void> {
    this.piwigoSaving = true;
    try {
      await BenevoleService.piwigoSetAccess(this.$route.params.id, level);
      this.piwigoAccess = level;
    } finally {
      this.piwigoSaving = false;
    }
  }

  async onSelectRole(roleId: string): Promise<void> {
    if (!this.data) return;
    this.roleSaving = true;
    this.data.role = roleId;
    try {
      await BenevoleService.update(this.data);
      await this.getDatas();
      this.hasUpdated = false;
    } finally {
      this.roleSaving = false;
    }
  }

  @Watch("data.prenom")
  @Watch("data.nom")
  @Watch("data.nom_usage")
  @Watch("data.mineur.statut")
  @Watch("data.mineur.heure_limite")
  onDataChange(): void {
    this.hasUpdated = true;
  }
}
